import { useCallback } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDocumentMailDraftInput,
  MailDraftResult,
  MailDraftStatus,
  AddArticlesToPoolInput,
  AddArticlesToPoolResult,
  ConvertPurchaseOrderInput,
  CreatePurchaseOrderInput,
  ItemResponse,
  ListResponse,
  OpenIncomingEntry,
  PurchaseDocumentKind,
  PurchaseOrderResponse,
  PurchaseOrderStatus,
  PurchaseOrderSummary,
  ReceivePurchaseOrderInput,
  ReplacePurchaseItemsInput,
  UpdatePurchaseOrderInput,
} from '@proerp/shared';
import { MAIL_DRAFT_KEY } from '@/features/settings/mailDraftSettingsApi';
import { apiRequest } from '@/lib/apiClient';

/** Query-Key-Präfix aller Bestell-Queries — exportiert, damit andere Features
 * (z. B. „Bestellung aus Projekt" in projects/materialApi) nach Schreib-
 * Operationen die Beschaffungs-Listen invalidieren können. */
export const PURCHASE_ORDERS_KEY = ['purchase-orders'] as const;
const PO_KEY = PURCHASE_ORDERS_KEY;

/**
 * Projekt-Sichten (u. a. Bedarfsliste: `orderedQty`, Beleg-Spalte, „im Bestellpool").
 * Freigeben/Zusammenfassen/Löschen einer (Pool-)Bestellung verändert sie — ohne
 * Invalidierung zeigte die Bedarfsliste bis zu 30 s lang den alten Stand.
 */
const PROJECTS_KEY = ['projects'] as const;

/**
 * Einkaufs-Kennzahlen (Anzahl je Status, offene Belege, offenes Bestellvolumen)
 * serverseitig über ALLE Belege der Standardliste
 * (`GET /v1/purchase-orders/summary`). Die Beschaffungs-Analyse zählte bis
 * 2026-09-29 die ersten 500 der Liste. Unter `PO_KEY` — Bestell-Mutationen
 * frischen es mit auf.
 */
export function usePurchaseOrderSummary() {
  return useQuery({
    queryKey: [...PO_KEY, 'summary'],
    queryFn: () => apiRequest<ItemResponse<PurchaseOrderSummary>>('/v1/purchase-orders/summary'),
  });
}

/** Offener Zulauf je Artikel aus laufenden Bestellungen (Bestandsübersicht). */
export function useOpenIncoming(articleIds: string[]) {
  const ids = [...articleIds].filter(Boolean).sort();
  return useQuery({
    queryKey: [...PO_KEY, 'open-incoming', ids],
    queryFn: () =>
      apiRequest<{ data: OpenIncomingEntry[] }>(
        `/v1/purchase-orders/open-incoming?articleIds=${ids.join(',')}`,
      ).then((r) => r.data),
    enabled: ids.length > 0,
    staleTime: 30_000,
  });
}

export interface PurchaseOrdersOpts {
  /** Mehrfachauswahl möglich; leer/undefined → alle Status. */
  status?: PurchaseOrderStatus[];
  kind?: PurchaseDocumentKind;
  sourceDocumentId?: string;
  /** Bestellpool (ADR 0041): true → nur POOL-Bestellungen; sonst werden POOL-Belege ausgeblendet. */
  pool?: boolean;
  page?: number;
  pageSize?: number;
  sort?: string;
}

function buildPurchaseQueryString(q?: string, projectId?: string, opts: PurchaseOrdersOpts = {}): string {
  const params = new URLSearchParams();
  params.set('page', String(opts.page ?? 1));
  params.set('pageSize', String(opts.pageSize ?? 100));
  if (q) params.set('q', q);
  if (projectId) params.set('projectId', projectId);
  if (opts.status?.length) params.set('status', opts.status.join(','));
  if (opts.kind) params.set('kind', opts.kind);
  if (opts.pool) params.set('pool', 'true');
  if (opts.sourceDocumentId) params.set('sourceDocumentId', opts.sourceDocumentId);
  if (opts.sort) params.set('sort', opts.sort);
  return params.toString();
}

/** Nicht-Hook-Fetch einer Bestell-Seite (für „Export alles" via collectAllPages). */
export function fetchPurchaseOrdersPage(
  q?: string,
  projectId?: string,
  opts: PurchaseOrdersOpts = {},
): Promise<ListResponse<PurchaseOrderResponse>> {
  return apiRequest<ListResponse<PurchaseOrderResponse>>(
    `/v1/purchase-orders?${buildPurchaseQueryString(q, projectId, opts)}`,
  );
}

/** `enabled: false` — z. B. das Projekt-Cockpit ohne `procurement.read` (fragt dann nicht an). */
export function usePurchaseOrders(
  q?: string,
  projectId?: string,
  { enabled = true, ...opts }: PurchaseOrdersOpts & { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...PO_KEY, { q, projectId, opts }],
    queryFn: () =>
      apiRequest<ListResponse<PurchaseOrderResponse>>(
        `/v1/purchase-orders?${buildPurchaseQueryString(q, projectId, opts)}`,
      ),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePurchaseOrder(id: string) {
  return useQuery({
    queryKey: [...PO_KEY, 'detail', id],
    queryFn: () => apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePurchaseOrderInput) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>('/v1/purchase-orders', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PO_KEY }),
  });
}

/**
 * Schreibzugriff auf eine Bestellung; invalidiert danach die Bestell-Queries.
 *
 * `invalidate: false` schaltet das ab — für den **Beleg-Editor**, der beim Speichern
 * mehrere Positionen nacheinander schreibt. Invalidiert jede einzelne Mutation, lädt
 * der Beleg je Position neu und die Seite baut den Editor über `key={adapter.revision}`
 * jedes Mal neu auf: die Positionen verschwinden und kommen einzeln zurück (gemeldet
 * 2026-07-31). Der Editor invalidiert stattdessen EINMAL am Ende.
 */
export type PoMutationOptions = { invalidate?: boolean };

function usePoMutation<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  options: PoMutationOptions = {},
  /** Weitere Query-Präfixe, die die Mutation verändert (z. B. Projekt-Bedarfslisten). */
  alsoInvalidate: readonly (readonly string[])[] = [],
) {
  const queryClient = useQueryClient();
  const shouldInvalidate = options.invalidate ?? true;
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (!shouldInvalidate) return;
      void queryClient.invalidateQueries({ queryKey: PO_KEY });
      for (const queryKey of alsoInvalidate) void queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Artikel aus dem Lagercockpit in den Bestellpool legen — eine Pool-Bestellung
 * je Standardlieferant (ADR 0041). Antwort nennt die angelegten Bestellungen.
 */
export function useAddArticlesToPool() {
  return usePoMutation((input: AddArticlesToPoolInput) =>
    apiRequest<ItemResponse<AddArticlesToPoolResult>>('/v1/purchase-orders/pool-items', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.data),
  );
}

/** Bestell-Queries neu holen — nachdem der Editor ALLE Positionen geschrieben hat. */
export function useInvalidatePurchaseOrders() {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: PO_KEY });
  }, [queryClient]);
}

/**
 * Antwort des eigenen Speicherns in den Cache legen, statt neu zu laden (ADR 0115) —
 * siehe `useStoreSalesDocument`. Übrige Bestell-Queries werden neu geholt.
 */
export function useStorePurchaseOrder(id: string) {
  const queryClient = useQueryClient();
  return useCallback(
    (order: PurchaseOrderResponse) => {
      queryClient.setQueryData<ItemResponse<PurchaseOrderResponse>>([...PO_KEY, 'detail', id], { data: order });
      void queryClient.invalidateQueries({
        queryKey: PO_KEY,
        predicate: (query) => !(query.queryKey[1] === 'detail' && query.queryKey[2] === id),
      });
    },
    [queryClient, id],
  );
}

export function useUpdatePurchaseOrder(id: string, options?: PoMutationOptions) {
  return usePoMutation(
    (input: UpdatePurchaseOrderInput) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    options,
  );
}

/**
 * Absender (bestellende Gesellschaft) wechseln, wenn Umnummerieren nicht mehr
 * geht: storniert die Bestellung und stellt sie unter der neuen Gesellschaft neu
 * aus. Liefert den NEUEN Beleg. Der einfache Fall (Entwurf, Nummer noch die
 * zuletzt vergebene) läuft über `useUpdatePurchaseOrder` mit `legalEntityId`.
 */
export function useChangePurchaseLegalEntity(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (legalEntityId: string) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}/change-legal-entity`, {
        method: 'POST',
        body: JSON.stringify({ legalEntityId }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PO_KEY }),
  });
}

export function useDeletePurchaseOrder() {
  return usePoMutation(
    (id: string) => apiRequest(`/v1/purchase-orders/${id}`, { method: 'DELETE' }),
    {},
    [PROJECTS_KEY],
  );
}

/** Bestellpool (ADR 0041): einzelne Pool-Bestellung freigeben (→ Bestellungen). */
export function useReleaseFromPool() {
  return usePoMutation(
    (id: string) => apiRequest(`/v1/purchase-orders/${id}/release-from-pool`, { method: 'POST' }),
    {},
    [PROJECTS_KEY],
  );
}

/** Bestellpool (ADR 0041): mehrere Pool-Bestellungen desselben Lieferanten zusammenfassen. */
export function useMergePool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderIds: string[]) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>('/v1/purchase-orders/merge-pool', {
        method: 'POST',
        body: JSON.stringify({ orderIds }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PO_KEY });
      void queryClient.invalidateQueries({ queryKey: PROJECTS_KEY });
    },
  });
}

/** Folgebelege zu einem Beleg (Belegfluss: sucht per sourceDocumentId). */
export function usePurchaseOrdersBySource(sourceDocumentId: string | null) {
  return useQuery({
    queryKey: [...PO_KEY, 'by-source', sourceDocumentId],
    queryFn: () =>
      apiRequest<ListResponse<PurchaseOrderResponse>>(
        `/v1/purchase-orders?${buildPurchaseQueryString(undefined, undefined, { sourceDocumentId: sourceDocumentId ?? undefined, pageSize: 50 })}`,
      ),
    enabled: Boolean(sourceDocumentId),
  });
}

/** „Beleg wandeln" — erzeugt einen Folgebeleg (Anfrage → Bestellung → Eingangsrechnung). */
export function useConvertPurchaseOrder(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetKind: PurchaseDocumentKind) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}/convert`, {
        method: 'POST',
        body: JSON.stringify({ targetKind } satisfies ConvertPurchaseOrderInput),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PO_KEY }),
  });
}

/**
 * Beleg duplizieren: neuer Beleg derselben Art mit allen Positionen, eigener
 * Nummer und Status „Entwurf" (kein Folgebeleg — s. Backend `duplicateOrder`).
 */
export function useDuplicatePurchaseOrder(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}/duplicate`, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PO_KEY }),
  });
}

export function useReceivePurchaseOrder(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceivePurchaseOrderInput = {}) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}/receive`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    // Auch nach einem Fehler neu laden: ein 409 („inzwischen schon eingebucht")
    // heißt, der angezeigte Stand ist veraltet (Doppelbuchungsschutz 2026-09-28).
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: PO_KEY });
      // Wareneingang bucht Lager → Artikel neu laden.
      void queryClient.invalidateQueries({ queryKey: ['articles'] });
      // Eingang wird an die Projekt-Materialliste zurückgemeldet → Projekte neu laden.
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/**
 * **Alle Positionen in einem Request ersetzen** — so speichert der Beleg-Editor
 * (Gegenstück zu `useReplaceSalesItems` im Verkauf). Soll-Zustand statt
 * Änderungsfolge; die Array-Reihenfolge ist die Belegreihenfolge.
 */
export function useReplacePurchaseItems(id: string, options?: PoMutationOptions) {
  return usePoMutation(
    (input: ReplacePurchaseItemsInput) =>
      apiRequest<ItemResponse<PurchaseOrderResponse>>(`/v1/purchase-orders/${id}/items`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    options,
  );
}

/**
 * Mail-Entwurf über Power Automate (ADR 0113, Nachtrag): eingerichtet? und Postfach.
 * Liegt unter dem gemeinsamen Präfix, damit Speichern in den Einstellungen ihn auffrischt.
 */
export function usePurchaseMailDraftStatus({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...MAIL_DRAFT_KEY, 'status', 'purchase'],
    queryFn: () => apiRequest<ItemResponse<MailDraftStatus>>('/v1/purchase-orders/mail-draft-status'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Einkaufsbeleg als Mail-Entwurf ins Postfach (nichts wird gesendet). */
export function useCreatePurchaseMailDraft(id: string) {
  return useMutation({
    mutationFn: (input: CreateDocumentMailDraftInput) =>
      apiRequest<ItemResponse<MailDraftResult>>(`/v1/purchase-orders/${id}/mail-draft`, { method: 'POST', body: JSON.stringify(input) }),
  });
}
